import React, { useRef } from 'react';
import type { CurrentUser, University, User } from '../types';
import AppHeader from '../components/layout/AppHeader';
import UniversityTable from '../components/universities/UniversityTable';
import UniversityForm from '../components/universities/UniversityForm';
import UserTable from '../components/users/UserTable';
import UserForm from '../components/users/UserForm';
import BulkUploadResults from '../components/users/BulkUploadResults';
import type { UniversityFormData, UserFormData, BulkUploadResults as BulkUploadResultsType } from '../types';

interface DashboardPageProps {
  currentUser: CurrentUser;
  universities: University[];
  filteredUsers: User[];
  onLogout: () => void;
  // University operations
  showUniversityModal: boolean;
  editingUniversity: University | null;
  onAddUniversity: (data: UniversityFormData) => Promise<void>;
  onEditUniversity: (data: UniversityFormData) => Promise<void>;
  onDeleteUniversity: (id: string) => Promise<void>;
  onOpenUniversityModal: (university?: University) => void;
  onCloseUniversityModal: () => void;
  // User operations
  showUserModal: boolean;
  editingUser: User | null;
  onAddUser: (data: UserFormData) => Promise<void>;
  onEditUser: (data: UserFormData) => Promise<void>;
  onDeleteUser: (userId: string) => Promise<void>;
  onOpenUserModal: (user?: User) => void;
  onCloseUserModal: () => void;
  // Bulk upload
  onBulkUpload: (file: File) => Promise<void>;
  showBulkUploadResults: boolean;
  bulkUploadResults: BulkUploadResultsType | null;
  onCloseBulkUploadResults: () => void;
}

const DashboardPage: React.FC<DashboardPageProps> = ({
  currentUser,
  universities,
  filteredUsers,
  onLogout,
  showUniversityModal,
  editingUniversity,
  onAddUniversity,
  onEditUniversity,
  onDeleteUniversity,
  onOpenUniversityModal,
  onCloseUniversityModal,
  showUserModal,
  editingUser,
  onAddUser,
  onEditUser,
  onDeleteUser,
  onOpenUserModal,
  onCloseUserModal,
  onBulkUpload,
  showBulkUploadResults,
  bulkUploadResults,
  onCloseBulkUploadResults,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isSuperAdmin = currentUser.role === 'super_admin';
  const universityName = universities.find(u => u.id === currentUser.universityId)?.name;

  const handleBulkUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onBulkUpload(file);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const userUniversities = isSuperAdmin
    ? universities
    : universities.filter(u => u.id === currentUser.universityId);

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader
        currentUser={currentUser}
        universityName={universityName}
        onLogout={onLogout}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {isSuperAdmin && (
            <div className="lg:col-span-5">
              <UniversityTable
                universities={universities}
                onAdd={() => onOpenUniversityModal()}
                onEdit={(uni) => onOpenUniversityModal(uni)}
                onDelete={onDeleteUniversity}
              />
            </div>
          )}

          <div className={isSuperAdmin ? 'lg:col-span-7' : 'lg:col-span-12'}>
            <UserTable
              users={filteredUsers}
              currentUser={currentUser}
              universities={universities}
              onAdd={() => onOpenUserModal()}
              onEdit={(user) => onOpenUserModal(user)}
              onDelete={onDeleteUser}
              onBulkUpload={handleBulkUploadClick}
            />
          </div>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,.xlsx,.xls"
        onChange={handleFileChange}
        className="hidden"
      />

      {showUniversityModal && (
        <UniversityForm
          university={editingUniversity}
          onSave={editingUniversity ? onEditUniversity : onAddUniversity}
          onClose={onCloseUniversityModal}
        />
      )}

      {showUserModal && (
        <UserForm
          user={editingUser}
          universities={userUniversities}
          currentUser={currentUser}
          onSave={editingUser
            ? (data) => onEditUser(data)
            : (data) => onAddUser(data)
          }
          onClose={onCloseUserModal}
        />
      )}

      {showBulkUploadResults && bulkUploadResults && (
        <BulkUploadResults
          results={bulkUploadResults}
          onClose={onCloseBulkUploadResults}
        />
      )}
    </div>
  );
};

export default DashboardPage;
